import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from "react-i18next";
import axiosInstance from '@/api/axios';
import { InfoTooltip } from './Infotooltip';

function getColorScheme(amount) {
  if (amount >= 1000) return { primary: '#10b981', text: 'text-green-600 dark:text-green-400' };
  if (amount >= 500)  return { primary: '#3b82f6', text: 'text-blue-500 dark:text-blue-400' };
  return                     { primary: '#f59e0b', text: 'text-yellow-600 dark:text-yellow-400' };
}

export default function WasteDiverted({ selectedBins, selectedTimePeriod, year, customDateRange }) {
  const [animatedAmount, setAnimatedAmount] = useState(0);
  const { t, i18n } = useTranslation();

  // Stable key for query + useEffect
  const customDateRangeKey = (() => {
    const r = customDateRange?.[0];
    if (!r?.startDate || !r?.endDate) return null;
    const start = r.startDate instanceof Date ? r.startDate.toISOString() : r.startDate;
    const end   = r.endDate   instanceof Date ? r.endDate.toISOString()   : r.endDate;
    return `${start}__${end}`;
  })();

  const isCustom = Boolean(customDateRangeKey);

  const { data, isLoading, error } = useQuery({
    queryKey: ['wasteDiverted', selectedBins, selectedTimePeriod, year, customDateRangeKey],
    queryFn: async () => {
      const params = { 'bins[]': selectedBins };

      if (isCustom) {
        const r = customDateRange[0];
        params.start_date = r.startDate instanceof Date ? r.startDate.toISOString() : r.startDate;
        params.end_date   = r.endDate   instanceof Date ? r.endDate.toISOString()   : r.endDate;
      } else {
        params.period = selectedTimePeriod;
        params.year   = year;
      }

      const response = await axiosInstance.get('waste_diverted/', { params });
      return response.data;
    },
    enabled: selectedBins.length > 0,
  });

  useEffect(() => {
    if (!data?.waste_diverted) return;
    const targetAmount = data.waste_diverted;
    if (animatedAmount === targetAmount) return;
    const timeout = setTimeout(() => {
      setAnimatedAmount(prev =>
        prev < targetAmount
          ? Math.min(prev + Math.ceil(targetAmount / 20), targetAmount)
          : Math.max(prev - Math.ceil(targetAmount / 20), targetAmount)
      );
    }, 20);
    return () => clearTimeout(timeout);
  }, [animatedAmount, data?.waste_diverted]);

  const colorScheme = getColorScheme(data?.waste_diverted || 0);

  // Date range label shown in header
  const getRangeLabel = () => {
    if (!data?.start_date || !data?.end_date) return null;
    // FIX: toLocaleDateString with no timeZone renders in the *browser's*
    // local timezone, not the server's. The backend already computes the
    // right date boundary in Europe/Berlin time — letting the browser
    // reinterpret that instant in its own timezone can roll it back a day.
    const fmt = (iso) => new Date(iso).toLocaleDateString(i18n.language, { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Europe/Berlin' });
    if (isCustom) return `${fmt(data.start_date)} – ${fmt(data.end_date)}`;
    switch (selectedTimePeriod) {
      case 'day':   return fmt(data.start_date);
      case 'month': return new Date(data.start_date).toLocaleDateString(i18n.language, { month: 'long', year: 'numeric', timeZone: 'Europe/Berlin' });
      case 'year':  return year?.toString();
      default:      return `${fmt(data.start_date)} – ${fmt(data.end_date)}`;
    }
  };

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0].payload;
    return (
      <div className="bg-white dark:bg-gray-800 p-2 border dark:border-gray-700 rounded-lg shadow text-xs">
        <p className="font-bold dark:text-gray-200 mb-1">{d.date}</p>
        <p className="text-green-600 dark:text-green-400">{t('wasteDiverted.tooltip.diverted')}: {d.diverted} kg</p>
        <p className="text-gray-500 dark:text-gray-400">{t('wasteDiverted.tooltip.residual')}: {d.residual} kg</p>
        <p className="text-gray-500 dark:text-gray-400">{t('wasteDiverted.tooltip.total')}: {d.total} kg</p>
      </div>
    );
  };

  if (isLoading) return (
    // Skeleton — header stays generic (bins not confirmed yet), number
    // line + bar-chart silhouette fill the same slots the real chart uses.
    <div className="rounded-lg border bg-card text-card-foreground w-full h-[200px] flex flex-col motion-safe:animate-pulse" aria-busy="true">
      <span className="sr-only" role="status">{t('wasteDiverted.loading')}</span>
      <div className="px-4 py-3 flex items-center justify-between">
        <div className="h-4 w-28 rounded bg-gray-200 dark:bg-slate-700" />
        <div className="h-5 w-5 rounded-full bg-gray-200 dark:bg-slate-700" />
      </div>
      <div className="px-4 pb-1">
        <div className="h-6 w-20 rounded bg-gray-200 dark:bg-slate-700" />
      </div>
      <div className="flex-1 px-4 pb-4 flex items-end gap-1.5">
        {[40, 65, 50, 80, 55, 70, 45, 60, 75, 50, 65, 55].map((h, i) => (
          <div key={i} className="flex-1 rounded-t bg-gray-200 dark:bg-slate-700" style={{ height: `${h}%` }} />
        ))}
      </div>
    </div>
  );

  if (error) return (
    <div className="rounded-lg border bg-card text-card-foreground w-full h-[200px] flex items-center justify-center">
      {t('wasteDiverted.error')}
    </div>
  );

  if (!data || selectedBins.length === 0) return (
    <div className="rounded-lg border bg-card text-card-foreground w-full h-[200px] flex items-center justify-center">
      {selectedBins.length === 0 ? t('wasteDiverted.selectBins') : t('wasteDiverted.noData')}
    </div>
  );

  return (
    <div className="rounded-lg border bg-card text-card-foreground dark:hover:bg-slate-900 hover:cursor-pointer flex flex-col w-full h-[200px]">
      <div className="px-4 py-3 flex items-center justify-between">
        <h2 className="tracking-tight text-sm font-medium">{t('wasteDiverted.title')}</h2>
        <div className="flex items-center gap-2">
          {isCustom && getRangeLabel() && (
            <span className="text-xs text-muted-foreground">{getRangeLabel()}</span>
          )}
          <InfoTooltip text="Total weight of waste diverted from residual by correct sorting into PMD, organic, or paper streams, excluding contaminated items." />
        </div>
      </div>

      <div className="px-4 pb-1 text-2xl font-bold text-green-600 dark:text-green-400">
        {animatedAmount.toLocaleString()} kg
      </div>

      <div className="flex-1 w-full px-0 pt-0 overflow-hidden rounded-b-lg">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data.historical_data} margin={{ top: 5, right: 10, left: 10, bottom: 20 }}>
            <XAxis
              dataKey="date"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 10, fill: '#9CA3AF' }}
              dy={10}
              interval="preserveStartEnd"
            />
            <YAxis hide />
            <Tooltip content={<CustomTooltip />} wrapperStyle={{ outline: 'none' }} />
            <Bar dataKey="diverted" radius={[4, 4, 0, 0]}>
              {data.historical_data.map((_, index) => (
                <Cell key={`cell-${index}`} fill="#10b981" />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}